import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Validators, FormGroup, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { CmgUiUtilities } from 'apps/cmg-clear2-ui/src/app/cmg-1003/components/common/cmg-ui-utils';
import { assembleLookups } from 'apps/cmg-clear2-ui/src/app/shared/components/subject-property-information/subject-property-information.utils';
import { SessionQuery, SessionStore, disableFormControlsBasedOnStatusOriginal } from 'apps/cmg-clear2-ui/src/app/core/state';
import {
  Cmg1003ApplicationInfoQuery,
  Cmg1003BorrowerQuery,
  Cmg1003FileDataQuery,
  Cmg1003FileDataStore,
  Cmg1003LoanOfficerQuery,
  Cmg1003LoanQuery,
  Cmg1003PropertyQuery,
  Cmg1003PropertyStore,
  Cmg1003StoreQueryService,
} from 'apps/cmg-clear2-ui/src/app/loan-state';
import { distinctUntilChanged, map, combineLatest, filter, tap, takeUntil, Subject, startWith, pairwise, switchMap, take, Observable } from 'rxjs';
import { CmgValidators } from 'apps/cmg-clear2-ui/src/app/shared/utils/required.validator';
import {
  InvalidControlModel,
  Cmg1003SectionTypes,
  BorrowerModel,
  AddressModel,
  ApplicationInfoModel,
} from 'apps/cmg-clear2-ui/src/app/cmg-1003/models';
import { containsNumbersOnly, findInvalidControls, resetValidations } from 'apps/cmg-clear2-ui/src/app/shared/utils';
import { generateDerivedStreetValue, generateDerivedValues } from './subject-property-information.utils';
import { counties } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models/lookups/common.lookups';
import { LoanStatus, LockStatusType } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models/cmg-1003-common.enum';
import { LoanSummarySectionTypes } from '../../../loan-summary/models/loan-summary-section-types.enum';
import { Cmg1003LookupsService } from '../../../cmg-1003/services/cmg-1003-lookups.service';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { applyPartialEnablingOrDisablingForSecondOrThirdLien } from '../../../core/utils';
import { SyncType } from '../../../models';
import { Cmg1003LoanStatusHistoryQuery } from '../../../loan-state/loan-status-history/loan-status-history.query';
import { Panel } from 'primeng/panel';
import { AsyncPipe } from '@angular/common';
import { Checkbox } from 'primeng/checkbox';
import { CmgFormControlDomDirective } from '../../directives/cmg-form-control-dom.directive';
import { Select } from 'primeng/select';
import { CmgValidatorMessagesComponent } from '../cmg-validator-messages/cmg-validator-messages.component';
import { InputText } from 'primeng/inputtext';
import { CmgFormControlZipCodeComponent } from '../cmg-form-control-zip-code/cmg-form-control-zip-code.component';
import { InputNumber } from 'primeng/inputnumber';
import { CmgPrimeNgInputNumberFocusDirective } from '../../directives/cmg-input-number-focus.directive';
import { CmgFormControlFactorResetDirective } from '../../directives/cmg-form-control-factor-reset.directive';
import { QaSlugPipe } from '../../pipes/qa-slug.pipe';

@Component({
  selector: 'cmg-clear2-ui-subject-property-information',
  templateUrl: './subject-property-information.component.html',
  styleUrls: ['./subject-property-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Panel,
    PrimeTemplate,
    ReactiveFormsModule,
    Checkbox,
    CmgFormControlDomDirective,
    Select,
    CmgValidatorMessagesComponent,
    InputText,
    CmgFormControlZipCodeComponent,
    InputNumber,
    CmgPrimeNgInputNumberFocusDirective,
    CmgFormControlFactorResetDirective,
    AsyncPipe,
    QaSlugPipe,
  ],
})
export class SubjectPropertyInformationComponent implements OnInit, OnDestroy {
  private readonly _fb = inject(FormBuilder);
  private readonly _borrowerQuery = inject(Cmg1003BorrowerQuery);
  private readonly _propertyStore = inject(Cmg1003PropertyStore);
  private readonly _propertyQuery = inject(Cmg1003PropertyQuery);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _lookupsService = inject(Cmg1003LookupsService);
  private readonly _messageService = inject(MessageService);
  private readonly _fileDataStore = inject(Cmg1003FileDataStore);
  private readonly _appInfoQuery = inject(Cmg1003ApplicationInfoQuery);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _loanOfficerQuery = inject(Cmg1003LoanOfficerQuery);
  private readonly _cmg1003LoanStatusHistoryQuery = inject(Cmg1003LoanStatusHistoryQuery);

  /** This is the last valid ZIP code that yielded a matching location. Used as fallback when ZIP code lookup fails.*/
  private _lastValidZipCode: { zip: string; state: string };
  showOccupancyType: boolean;
  lookups = assembleLookups();
  presentAddress: AddressModel;
  private get _primaryBorrower(): BorrowerModel {
    let primaryBorrower = null;
    const primaryBorrowers = this._borrowerQuery.getAll({
      limitTo: 1,
      filterBy: (entity) => entity.isPrimaryBorrower === true,
    });

    if (primaryBorrowers.length > 0) {
      primaryBorrower = primaryBorrowers[0];
    }
    return primaryBorrower;
  }
  /** The application with 'displayOrder' of 1. */
  primaryApplication$ = this._appInfoQuery.selectAll().pipe(
    filter((appInfos) => appInfos.length > 0),
    map((appInfos) => appInfos.filter((app) => app.displayOrder === 1)[0]),
  );

  propertyInfo$ = this._propertyQuery.select().pipe(distinctUntilChanged());
  loanPurpose$ = this._loanQuery.select().pipe(
    map((loan) => loan.loanPurpose),
    distinctUntilChanged(),
  );

  /** Originates from FileData. */
  occupancyType$ = this._fileDataQuery.select().pipe(
    map((file) => file.occupancyType),
    distinctUntilChanged(),
  );
  /** Determines whether forms are disabled. */
  isFullyDisabled$ = this._storeQuery.statusRequiresDisabling$();

  /** Fields that are always disabled when Loan is in a Locked Status */
  forceDisableOnLockKeys = ['occupancyType', 'propertyType'];
  disableSameAsPresAddr$: Observable<boolean>;
  activeApplication: ApplicationInfoModel;

  /** This is a snapshot of the propertyForm. Mainly used for a workaround for valueChanges. */
  propertyFormSnapshot;
  propertyForm: FormGroup = this._fb.group(
    {
      city: [null, { updateOn: 'blur' }],
      county: [Validators.required],
      sameAsPresentAddress: [false],
      addressConfidentiality: [false],
      isPropertyAddressTBD: [],
      giftsForUi: this._fb.array([]),
      loanPurpose: [], // For UI only. Intended for LoanStore.
      occupancyType: ['', [CmgValidators.required(0)]], // For UI only. Intended for FileDataStore.
      propertyType: ['', [CmgValidators.required(0)]],
      state: [null, { updateOn: 'blur', validators: Validators.required }],
      street: [null, { updateOn: 'blur', validators: Validators.required }],
      zip: [null, { updateOn: 'blur', validators: Validators.required }],
      streetforUI: [null, { updateOn: 'blur', validators: Validators.required }],
      vacancyFactor: [],
      grossRentalIncome: [null, { updateOn: 'blur' }],
    },
    { updateOn: 'change' },
  );
  private readonly _destroyed$ = new Subject<boolean>();
  private readonly _loanStatus = LoanStatus;
  private _isClear2DisabledForLoan$ = this._storeQuery.isClear2DisabledForLoan$;

  constructor() {
    this.propertyForm
      .get('occupancyType')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((investmentPropValue) => {
        if (investmentPropValue === 3) {
          this.showOccupancyType = true;
        } else {
          this.showOccupancyType = false;
        }
      });
  }

  ngOnInit() {
    this._initializeLastValidZipCode();

    combineLatest([this.propertyInfo$, this.occupancyType$, this.isFullyDisabled$])
      .pipe(
        filter(([property]) => !!property.subPropId),
        tap(([propertyInfo, occupancyType]) => {
          this.propertyForm.patchValue(
            { ...propertyInfo, ...generateDerivedValues(propertyInfo, occupancyType), ...generateDerivedStreetValue(propertyInfo) },
            { emitEvent: false },
          );
          disableFormControlsBasedOnStatusOriginal(
            [this.propertyForm],
            this._sessionQuery,
            this._storeQuery,
            this.forceDisableOnLockKeys,
            null,
            () => {
              this._applyPartialDisabling();
            },
          );
          this.lookups.counties = counties.filter((county) => county.value === propertyInfo.state || county.name === 'Select');
          this.propertyFormSnapshot = this.propertyForm.value;
        }),
      )
      .pipe(takeUntil(this._destroyed$))
      .subscribe((results) => {
        if (results[2]) {
          applyPartialEnablingOrDisablingForSecondOrThirdLien(
            false,
            ['sameAsPresentAddress', 'streetforUI'],
            this.propertyForm,
            this._sessionQuery,
            this._fileDataQuery,
          );
        }
      });

    this.propertyForm
      .get('zip')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((newZip) => {
        this._onZipCodeChanged(newZip);
      });

    this.propertyForm
      .get('occupancyType')
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe((newOccupancyType) => this._fileDataStore.update({ occupancyType: newOccupancyType }));
    if (this.propertyForm.value['occupancyType'] === 3) {
      this.showOccupancyType = true;
    } else {
      this.showOccupancyType = false;
    }
    this.propertyForm.valueChanges
      .pipe(
        takeUntil(this._destroyed$),
        startWith(this.propertyForm.value),
        pairwise(),
        map(([oldValues, newValues]) => {
          if (
            this.propertyFormSnapshot &&
            Object.keys(this.propertyFormSnapshot).every((ssKey) => this.propertyFormSnapshot[ssKey] === newValues[ssKey])
          ) {
            return;
          }
          const prevState = Object.keys(oldValues).every((key) => !oldValues[key] || oldValues[key]?.length === 0)
            ? this.propertyFormSnapshot
            : oldValues;

          if (!prevState) {
            return;
          }
          const key = Object.keys(prevState).find((k) => {
            return newValues[k] !== prevState[k];
          });

          return {
            key: key,
            oldValue: prevState[key],
            newValue: newValues[key],
          };
        }),
      )
      .subscribe((changeObj) => {
        if (changeObj?.key) {
          this._onPropertyFormChanges();
        }
      });

    this._fileDataQuery
      .select((f) => f.syncType)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((syncType) => {
        if (syncType === SyncType.SecondLien || syncType === SyncType.ThirdLien) {
          this.propertyForm.disable({ emitEvent: false });
        }
      });

    this._cmg1003LoanStatusHistoryQuery
      .select((s) => s)
      .pipe(take(1))
      .subscribe((loanStatusInfo) => {
        if (loanStatusInfo.preapprovalApplicationDate || loanStatusInfo.creditOnlyDate) {
          this._disableControls(['loanPurpose', 'state']);
        }
      });

    this._isClear2DisabledForLoan$.subscribe((isClear2DisabledForLoan) => {
      if (isClear2DisabledForLoan) {
        this.propertyForm.disable({ emitEvent: false });
      }
    });
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }
  // For: CmgComponentActions
  getSectionType(): LoanSummarySectionTypes {
    return LoanSummarySectionTypes.propertyInformation;
  }

  // For CmgComponentActions
  validate(): void {
    CmgUiUtilities.markFormAsDirty(this.propertyForm);
    CmgUiUtilities.markFormAsTouched(this.propertyForm);

    const assembleErrorMessage = (key: string, controlError: InvalidControlModel) => {
      switch (key) {
        case 'county':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'County - Required';
          }
          break;
        case 'state':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'State - Required';
          }
          break;
        case 'street':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Street Address - Required';
          }
          break;
        case 'occupancyType':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Occupancy Type - Required';
          }
          break;
        case 'propertyType':
          if (controlError.errors['required']) {
            controlError.invalidMessage = 'Property Type - Required';
          }
          break;
        case 'zip':
          if (controlError.errors['required']) {
            controlError.controlId = 'zip-input';
            controlError.invalidMessage = 'Zip - Required';
          }
          break;
        default:
          break;
      }
    };

    this._sessionStore.setValidationError(Cmg1003SectionTypes.propertyInformation, findInvalidControls(this.propertyForm, assembleErrorMessage));
    this._cdRef.detectChanges();
  }

  /** Updates city, state, and county, based on entered ZIP code. */
  private readonly _onZipCodeChanged = (zipCode: string) => {
    // [Scenario]: Brand new 1003, where a valid ZIP code has never been assigned.
    if (!this._lastValidZipCode?.zip && !zipCode?.trim()) {
      return;
    }

    // [Scenario]: User deleted ZIP code, or ZIP code is invalid - e.g. non-numeric characters, invalid length.
    if (!zipCode?.trim() || zipCode?.length < 5 || !containsNumbersOnly(zipCode)) {
      this._correctInvalidZipCode();
      return;
    }

    // [Scenario]: Acceptable ZIP code is entered.
    this._lookupsService
      .getCityStateByZipCode(zipCode.trim())
      .pipe(
        take(1),
        tap((matchingZip) => {
          this._sessionQuery
            .select((s) => s.loanStatusId)
            .pipe(take(1))
            .subscribe((loanStatusId) => {
              if (loanStatusId === this._loanStatus.PreApproved && this.propertyForm.get('state').value !== matchingZip.state) {
                this._handleInvalidZipCode(
                  'The State cannot be changed once the File has been Pre-Approved. If you need to change the State on a Pre-Approved File, you will need to Approve Not Accept your file and create a new file to proceed.',
                );
              } else {
                this._loanOfficerQuery
                  .select((lo) => lo.licensedStates)
                  .pipe(take(1))
                  .subscribe((states) => {
                    // [Scenario]: User entered Zip Code for a state which they do not have a license for
                    if (!states || !states.includes(matchingZip.state)) {
                      this._handleInvalidZipCode('Zip Code cannot be changed because Loan Officer does not have an active license in the state.');
                      return;
                    }

                    // [Scenario]: User entered a valid ZIP code and the state is one that they have a license for
                    if (matchingZip.zipCodeID) {
                      this._lastValidZipCode = {
                        zip: matchingZip.zipCode,
                        state: matchingZip.state,
                      };
                      this.propertyForm.get('city').setValue(matchingZip.city, { emitEvent: false });
                      this.propertyForm.get('state').setValue(matchingZip.state, { emitEvent: false });
                      this.propertyForm.get('county').setValue(matchingZip.county, { emitEvent: false });
                      this._onPropertyFormChanges();
                    }
                    // [Scenario]: User entered a ZIP code with no matching lookup.
                    else {
                      this._correctInvalidZipCode();
                    }
                  });
              }
            });
        }),
      )
      .subscribe();
  };

  private _handleInvalidZipCode(msg: string) {
    this._messageService.add({
      severity: 'error',
      summary: '',
      detail: msg,
      key: 'loan-summary',
    });
    this._correctInvalidZipCode();
  }

  private _initializeLastValidZipCode() {
    this.propertyInfo$
      .pipe(
        filter((property) => !!property.zip),
        switchMap((property) => {
          return this._lookupsService.getCityStateByZipCode(property.zip).pipe(
            tap((matchingZip) => {
              if (matchingZip.zipCodeID && matchingZip.state === property.state) {
                this._lastValidZipCode = {
                  zip: matchingZip.zipCode,
                  state: matchingZip.state,
                };
              }
            }),
          );
        }),
        take(1),
      )
      .subscribe();
  }

  /** Reverts ZIP code to last valid one. */
  private _correctInvalidZipCode() {
    this.propertyForm.get('zip').setValue(this._lastValidZipCode?.zip, { emitEvent: false });
    this._propertyStore.update({ zip: this._lastValidZipCode?.zip });
  }

  /** Updates property store on form change. */
  private _onPropertyFormChanges() {
    const propertyInfo = this.propertyForm.getRawValue();

    propertyInfo.street = propertyInfo.streetforUI;

    this._propertyStore.update(propertyInfo);
  }

  /** Partially disables some form controls, based on status(es). */
  private _applyPartialDisabling() {
    const session = this._sessionQuery.getValue();

    if (session.rateLockStatus === LockStatusType.Locked) {
      if (!this.propertyForm.get('isPropertyAddressTBD').value) {
        this._disableControls(['isPropertyAddressTBD', 'streetforUI', 'zip', 'city', 'county', 'loanPurpose', 'occupancyType', 'propertyType']);
      }
    } else {
      /**  [Scenario]: File not Locked --> Look at Loan Status. */
      switch (session.loanStatusId) {
        case LoanStatus.Prospect:
        case LoanStatus.Sandbox:
        case LoanStatus.LePending:
          this.updateTBDAddressFields(true);
          break;
        case LoanStatus.CreditOnly:
        case LoanStatus.PreApproved:
          this._disableControls(['state']);
          this.updateTBDAddressFields(true);
          break;

        case LoanStatus.Disclosed:
          if (!this.propertyForm.get('isPropertyAddressTBD').value) {
            this._disableControls(['isPropertyAddressTBD', 'zip', 'city', 'county', 'sameAsPresentAddress', 'streetforUI', 'state']);
          }
          break;
        default:
          break;
      }
    }
    this._disableControls(['state']);
  }

  /** Generic function to disable specified controls, with emitEvent: false. */
  private _disableControls(controlNames: string[]) {
    controlNames.forEach((control) => {
      this.propertyForm.get(control).disable({ emitEvent: false });
    });
  }

  private _enableControls(controlNames: string[]) {
    controlNames.forEach((control) => {
      this.propertyForm.get(control).enable({ emitEvent: false });
    });
  }

  updateAddress(isNgOnInit: boolean) {
    const tbdCheckbox = this.propertyForm.get('sameAsPresentAddress');
    if (this._primaryBorrower) {
      this.presentAddress = this._primaryBorrower.presentAddress;
    }
    if (!isNgOnInit && tbdCheckbox.value && this.presentAddress) {
      this.propertyForm.get('streetforUI').setValue(this._primaryBorrower.presentAddress?.street);
      this.propertyForm.get('city').setValue(this._primaryBorrower.presentAddress.city);
      this.propertyForm.get('zip').setValue(this._primaryBorrower.presentAddress.zipCode);
      this.propertyForm.get('county').setValue(this._primaryBorrower.presentAddress.county);
    }

    if (tbdCheckbox.value) {
      this._disableControls(['zip', 'streetforUI', 'city', 'county']);
    } else {
      this._enableControls(['zip', 'streetforUI', 'city', 'county']);
    }
  }

  /**
   * Manages disabled state of controls that depend on "Property Address TBD" checkbox.
   * NOTE: Street will use an empty string, instead of null, since Byte ignores null during saves.
   * @param isNgOnInit Represents whether updateTBDAddressFields() was called by ngOnInit. Safeguards against infinite loops from 'emitEvent'.
   */
  updateTBDAddressFields(isNgOnInit: boolean) {
    const targetControls: { name: string; disabledValue: string | boolean }[] = [
      { name: 'streetforUI', disabledValue: 'TBD' },
      { name: 'sameAsPresentAddress', disabledValue: 'false' },
    ];

    const tbdCheckbox = this.propertyForm.get('isPropertyAddressTBD');

    if (tbdCheckbox.value) {
      targetControls.forEach((control) => {
        this.propertyForm.get(control.name).disable({ emitEvent: false });
        this.propertyForm.get(control.name).setValue(control.disabledValue, { emitEvent: false });
      });

      if (!isNgOnInit) {
        const propertyInfo = this.propertyForm.getRawValue();
        this.propertyForm.get('street').setValue(propertyInfo.streetforUI, { emitEvent: false });
        this._propertyStore.update(this.propertyForm.getRawValue());
      }
    } else {
      targetControls.forEach((field) => {
        this.propertyForm.get(field.name).enable({ emitEvent: false });
      });

      if (!isNgOnInit) {
        this.propertyForm.get('street').setValue('');
        this.propertyForm.get('streetforUI').setValue('');
        this.propertyForm.get('streetforUI').markAllAsTouched();
      }
    }
  }

  resetValidations(): void {
    resetValidations(this.propertyForm);
    setTimeout(() => {
      this._cdRef.detectChanges();
    }, 100);
  }
}
